import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from './firebase';

const firstNonEmpty = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const SPNListPageMediaItems = ({ item, onPress, isSelected }) => {
  const rawArtworkUrl = useMemo(
    () => firstNonEmpty(item, [
      'ArtworkURL','artworkURL','artworkUrl',
      'localArtworkURL','localArtworkUrl',
      'thumbnailUrl','coverUrl','imageUrl'
    ]),
    [item]
  );

  const artistName = useMemo(
    () => firstNonEmpty(item, [
      'ArtistName','artistName','artist','creatorName','username','displayName','LabelName'
    ]),
    [item]
  );

  const titleToDisplay = useMemo(() => {
    if (item?.isAlbum) return firstNonEmpty(item, ['albumName','AlbumName','MovieTitle','title','name']);
    return firstNonEmpty(item, ['SongName','songName','MovieTitle','VideoName','title','name','trackTitle','trackName']);
  }, [item]);

  const [artUrl, setArtUrl] = useState('');
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!rawArtworkUrl) { setArtUrl(''); return; }
        if (rawArtworkUrl.startsWith('gs://')) {
          const parts = rawArtworkUrl.split('gs://')[1];
          const path = parts?.split('/').slice(1).join('/') || '';
          const url = await getDownloadURL(ref(storage, path));
          if (alive) setArtUrl(url);
          console.debug('[SPNListPageMediaItems] Using gs:// artwork', { id: item?.contentId ?? item?.id, url });
        } else {
          if (alive) setArtUrl(rawArtworkUrl);
          console.debug('[SPNListPageMediaItems] Using artwork URL', { id: item?.contentId ?? item?.id, url: rawArtworkUrl });
        }
      } catch (e) {
        console.error('[SPNListPageMediaItems] Artwork resolve error', { id: item?.contentId ?? item?.id, rawArtworkUrl, e });
        if (alive) setArtUrl('');
      }
    })();
    return () => { alive = false; };
  }, [rawArtworkUrl, item]);

  const wrapRef = useRef(null);

  useEffect(() => {
    const r = wrapRef.current?.getBoundingClientRect?.();
    console.debug('[SPNListPageMediaItems] Render item', {
      id: item?.contentId ?? item?.id,
      isSelected,
      artistName,
      titleToDisplay,
      hasArt: !!artUrl,
      rect: r && { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
    });
  }, [item, isSelected, artistName, titleToDisplay, artUrl]);

  return (
    <div
      ref={wrapRef}
      className="spn-visibility-reset spn-row"
      style={{ ...styles.mediaItem, ...(isSelected ? styles.selected : {}) }}
      onClick={onPress}
      role="button"
      tabIndex={0}
      data-content-id={item?.contentId ?? item?.id}
    >
      <div style={styles.mediaRow}>
        <div
          style={{
            ...styles.artWrap,
            backgroundImage: artUrl && !imgError ? `url("${artUrl}")` : 'none',
          }}
        >
          {artUrl && !imgError ? (
            <img
              src={artUrl}
              alt={`${artistName || 'artwork'}`}
              style={styles.songArtwork}
              loading="lazy"
              decoding="async"
              onError={() => { setImgError(true); console.warn('[SPNListPageMediaItems] <img> onError', { id: item?.contentId ?? item?.id, artUrl }); }}
              onLoad={(e) => { console.debug('[SPNListPageMediaItems] <img> onLoad', { id: item?.contentId ?? item?.id, w: e?.target?.naturalWidth, h: e?.target?.naturalHeight }); }}
            />
          ) : (
            <div style={styles.songArtworkPlaceholder}/>
          )}
        </div>

        <div style={styles.textContainer}>
          <div className="spn-artist" style={styles.artistName} title={artistName}>
            {artistName || '(Unknown Artist)'}
          </div>
          <div className="spn-title" style={styles.songName} title={titleToDisplay}>
            {titleToDisplay || '(Untitled)'}
          </div>
        </div>
      </div>

      <div style={styles.borderContainer}><div style={styles.centeredBorder} /></div>

      {/* Row-scoped color enforcement (wins against page styles) */}
      <style>{`
        .spn-row .spn-artist {
          color:#ffffff !important;
          -webkit-text-fill-color:#ffffff !important;
        }
        .spn-row .spn-title {
          color:#b8b8b8 !important;
          -webkit-text-fill-color:#b8b8b8 !important;
        }
        /* keep visibility/mix/reset but don't touch text color */
        .spn-visibility-reset, .spn-visibility-reset * {
          opacity: 1 !important;
          visibility: visible !important;
          mix-blend-mode: normal !important;
          filter: none !important;
        }
        .spn-row { position: relative; z-index: 1000000; }
        .spn-visibility-reset img { display:block !important; }
      `}</style>
    </div>
  );
};

const ART = 72;

const styles = {
  mediaItem: {
    position: 'relative',
    isolation: 'isolate',
    zIndex: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '10px 12px',
    margin: '6px 0',
    width: '100%',
    boxSizing: 'border-box',
    background: 'transparent',
    cursor: 'pointer',
    minHeight: ART + 12,
  },
  selected: {
    background: 'rgba(189, 252, 251, 0.15)',
    outline: '2px solid rgba(189, 252, 251, 0.45)',
    outlineOffset: 0,
    borderRadius: 8,
  },
  mediaRow: {
    display: 'grid',
    gridTemplateColumns: `${ART}px 1fr`,
    alignItems: 'center',
    width: '100%',
    columnGap: 12,
  },
  artWrap: {
    width: ART, height: ART, borderRadius: 8,
    backgroundColor: '#0b0b12',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
    position: 'relative',
  },
  songArtwork: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, position:'relative', zIndex:2 },
  songArtworkPlaceholder: { width: '100%', height: '100%', borderRadius: 8, background: 'linear-gradient(135deg,#1f1f2e 0%,#111122 100%)' },
  textContainer: { minWidth: 0 },
  artistName: {
    margin: 0, marginBottom: 6,
    fontSize: 16, fontWeight: 700, textAlign: 'left',
    fontFamily: 'brhendrix, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.25',
  },
  songName: {
    margin: 0, fontSize: 14, textAlign: 'left', fontWeight: 400,
    fontFamily: 'brhendrix, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.25',
  },
  borderContainer: { width: '100%', alignItems: 'center', marginTop: 4 },
  centeredBorder: { marginTop: 6, marginLeft: ART + 12, width: `calc(100% - ${ART + 12}px)`, borderBottom: '1px solid rgba(189, 252, 251, 0.3)' },
};

export default SPNListPageMediaItems;
